import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { IntlProvider } from 'react-intl'
import ReactGA from 'react-ga4'
import { hotjar } from 'react-hotjar'
import TagManager from 'react-gtm-module'

import { CssBaseline, LinearProgress } from '@mui/material'

import Header from '../Header'
import Footer from '../Footer'
import ButtonScrollToTop from '../ButtonScrollToTop'
import LayoutAuth from '../LayoutAuth'
import { CurrentLocale } from '@/src/CurrentLocale'
import { getTranslations } from '@/lib/getTranslations'
import getListRoutes from '@/src/getListRoutes'
import logger from '@/lib/logger'
import { captureMailjetCampaignLanding } from '@/lib/mailjetCampaignTracking'
import NoSsr from '@mui/material/NoSsr'
import Maintenance from '@/components/Maintenance'
import useMaintenance from '@/lib/hook/useMaintenance'

import { getDomainSetting } from '@/lib/getDomainSettings'
import getEnvVar from '@/lib/getEnvVar'
import useProfile from '@/lib/hook/useProfile'
import ProfileContext from '@/components/Context/ProfileContext'
import useBasket from '@/lib/hook/useBasket'
import POT_MANAGER from '@/lib/basket/POT_MANAGER'
import ThemeProviderByProfile from '@/components/Common/Providers/ThemeProviderByProfile'
import LayoutStyle from '@/components/Common/Layout/LayoutStyle'
import { ToastContainer } from 'react-toastify'
import HeaderTop from '@/components/Common/Header/HeaderTop'
import useBasketCounterProducts from '@/lib/hook/useBasketCounter'
import CookieRgpd from './CookieRgpd'
import Card from '../Card'
import DevelopmentBanner from '@/components/Common/DevBanner/DevelopmentBanner'

type Props = {
  locale: string
  host: string
  children: any
  usePaperLayout?: boolean
}
const isBrowser = typeof window !== 'undefined'

const Layout: React.FC<Props> = ({ children, locale, host }) => {
  const isInMaintenance = useMaintenance()

  const [openDrawer, setOpenDrawer] = useState<boolean>(false)

  const {
    profile,
    loadingProfile,
    dataLanguagesAvailable,
    refetch: profileRefetcher,
    isAuthenticated,
  } = useProfile({ host })

  POT_MANAGER.checkProfile(profile)

  const {
    data: basket,
    loading: basketLoading,
    refetch: refetchBasket,
    shippingIds,
    setShippingIds,
    loadingRefetchBasket,
  } = useBasket({ isAuthenticated })

  const { dataCounterProducts, refetchCounterProducts, loadingCounterProducts } =
    useBasketCounterProducts({ isAuthenticated })

  const router = useRouter()

  useEffect(() => {
    if (isBrowser && router.isReady) {
      captureMailjetCampaignLanding(router.asPath)
    }
  }, [router.asPath, router.isReady])

  // Reproduit getTranslations (module local synchrone, sans reseau). Recalcule sur changement de locale.
  const translations = useMemo(() => getTranslations(locale), [locale])

  // Coalescing : si un rechargement complet est deja en cours, les appels
  // supplementaires se regroupent en UN seul re-run a la fin (au lieu
  // d'empiler des cart_content de plusieurs secondes chacun).
  const basketRefetchInFlight = React.useRef<Promise<void> | null>(null)
  const basketRefetchPending = React.useRef(false)

  const basketAllRefetcher = async (): Promise<void> => {
    if (basketRefetchInFlight.current) {
      basketRefetchPending.current = true
      return basketRefetchInFlight.current
    }

    basketRefetchInFlight.current = (async () => {
      try {
        do {
          basketRefetchPending.current = false
          // Sequential refetch evite concurrent fetch cache ping-pong
          await refetchBasket()
          await refetchCounterProducts()
          // Rafraichit aussi le profil : apres saisie du RIB Outre-mer (customer_iban), le checkout
          // doit voir le RIB present (sinon l'avertissement "Renseigner mon rib" reste affiche).
          await profileRefetcher()
        } while (basketRefetchPending.current)
      } finally {
        basketRefetchInFlight.current = null
      }
    })()

    return basketRefetchInFlight.current
  }

  // Hotjar ne supporte pas d'etre initialise plusieurs fois dans une meme page : chaque
  // appel reinjecte le script `hotjar-<id>.js` ET ecrase `window._hjSettings`. Or l'effet
  // ci-dessous re-tourne a chaque changement de `profile?.user_id` (visiteur anonyme au
  // montage -> profil charge). En production `hjid` (2031579) et `hjidAeliaStore` (6538384)
  // sont differents et `groupAeliaStore` vient du compte, pas du domaine : un utilisateur
  // du groupe Aelia sur un domaine non-Aelia bootait donc deux sites Hotjar concurrents
  // dans le meme onglet, qui se partagent `window.hj` et le meme storage.
  // Invisible en dev/staging ou les deux ids sont identiques.
  const hotjarInitializedRef = React.useRef(false)

  useEffect(() => {
    if (isBrowser) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(getDomainSetting('facebookId'))
          ReactPixel.pageView()
          router.events.on('routeChangeComplete', () => {
            ReactPixel.track('ViewContent', router['asPath'])
            ReactPixel.pageView()
          })
        })

      ReactGA.initialize(getDomainSetting('googleId'), {
        gaOptions: {
          userId: profile?.user_id,
          debug_mode: getEnvVar('APP_ENV') !== 'production',
        },
        gtagOptions: {
          debug_mode: getEnvVar('APP_ENV') !== 'production',
        },
      })

      const tagManagerArgs = {
        gtmId: getDomainSetting('googleTagManager'),
        dataLayer: {
          userId: profile?.user_id,
          userProject: 'pharmedistore',
        },
      }
      TagManager.initialize(tagManagerArgs)
      if (!hotjarInitializedRef.current) {
        hotjarInitializedRef.current = true
        if (profile.groupAeliaStore) {
          hotjar.initialize({
            id: getDomainSetting('hjidAeliaStore'),
            sv: getDomainSetting('hjsv'),
          })
        } else {
          hotjar.initialize({ id: getDomainSetting('hjid'), sv: getDomainSetting('hjsv') })
        }
      }
    }
  }, [profile?.user_id])

  /* useEffect(() => {
    if (!!isBrowser) {
      Crisp.configure(SETTINGS.crisp.CRISP_WEBSITE_ID)
      Crisp.user.setEmail(profile.email)
      Crisp.user.setNickname(`${profile.firstname} ${profile.lastname}`)
    }
  }, [])*/

  useEffect(() => {
    if (isBrowser) {
      ReactGA.send({
        hitType: 'pageview',
        page: router['asPath'],
        title: router['asPath'],
        user_id: profile?.user_id,
      })
    }
  }, [router['route'], profile?.user_id])

  if (loadingProfile || isInMaintenance === null) return <LinearProgress />
  const tabPage = router['route'].replace('/[locale]/', '').split('[')

  const activeLayoutAuth =
    !!isAuthenticated &&
    !!router &&
    getListRoutes
      .filter(i => !!i.menuAccount)
      .find(i => {
        const regexNameRoute = new RegExp(
          i.href.replace('/', '').replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/gi, '\\$&'),
          'i'
        )
        return regexNameRoute.test(tabPage[0])
      })

  const onMenuClick = (e: any) => {
    e.stopPropagation()
    e.persist()
    setOpenDrawer(!openDrawer)
  }

  const isIE11 =
    // @ts-ignore
    typeof window !== 'undefined' && !!window.MSInputMethodContext && !!document.documentMode

  return (
    <IntlProvider
      locale={locale}
      messages={translations}
      defaultLocale={locale}
      onError={logger.warn}
    >
      <ProfileContext.Provider
        value={{
          profile,
          isAuthenticated,
          loadingProfile,
          dataLanguagesAvailable,
          profileRefetcher,
          basket,
          basketLoading,
          loadingRefetchBasket,
          basketAllRefetcher,
          shippingIds,
          setShippingIds,
          dataCounterProducts,
          loadingCounterProducts,
          refetchCounterProducts,
        }}
      >
        <DevelopmentBanner />
        <ThemeProviderByProfile profile={profile}>
          <CurrentLocale />
          <LayoutStyle>
            <HeaderTop isInMaintenance={isInMaintenance} onMenuClick={onMenuClick} />
          </LayoutStyle>
          <CssBaseline />
          <ToastContainer
            draggablePercent={90}
            position="bottom-center"
            autoClose={3000}
            hideProgressBar={false}
            limit={2}
            className="override-toast"
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            {isInMaintenance ? (
              <>
                <Header
                  isInMaintenance={isInMaintenance}
                  openDrawer={false}
                  setOpenDrawer={setOpenDrawer}
                />
                <Maintenance />
              </>
            ) : (
              <>
                <LayoutStyle>
                  {!!children && (
                    <Card elevate>
                      {isAuthenticated && (
                        <Header
                          isInMaintenance={isInMaintenance}
                          openDrawer={openDrawer}
                          setOpenDrawer={setOpenDrawer}
                        />
                      )}
                      <div style={{ width: '100%', overflowY: isIE11 ? 'auto' : undefined }}>
                        {activeLayoutAuth ? <LayoutAuth>{children}</LayoutAuth> : <>{children}</>}
                      </div>
                    </Card>
                  )}
                  <Footer />
                </LayoutStyle>

                <CookieRgpd />
                <NoSsr>
                  <ButtonScrollToTop />
                </NoSsr>
              </>
            )}
          </div>
        </ThemeProviderByProfile>
      </ProfileContext.Provider>
    </IntlProvider>
  )
}

export default Layout
